import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { translateText } from '@/lib/api';
import { useI18n } from '@/lib/i18n';

const LANGUAGE_OPTIONS = [
    { value: 'en', label: 'English' },
    { value: 'ja', label: '日本語' },
    { value: 'it', label: 'Italiano' },
    { value: 'fr', label: 'Français' },
    { value: 'es', label: 'Español' },
    { value: 'de', label: 'Deutsch' },
    { value: 'zh', label: '中文' },
    { value: 'ko', label: '한국어' },
    { value: 'pt', label: 'Português' },
];

type TranslateModalProps = {
    isOpen: boolean;
    onClose: () => void;
    sourceText: string;
    defaultTarget?: string;
};

export function TranslateModal({ isOpen, onClose, sourceText, defaultTarget }: TranslateModalProps) {
    const { lang, t } = useI18n();
    const [targetLang, setTargetLang] = useState(defaultTarget || (lang === 'en' ? 'ja' : 'en'));
    const [translated, setTranslated] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!isOpen) return;
        setTranslated('');
        setError('');
        setLoading(false);
        setTargetLang(defaultTarget || (lang === 'en' ? 'ja' : 'en'));
    }, [isOpen, defaultTarget, lang]);

    const handleTranslate = async () => {
        if (!sourceText.trim()) return;
        setLoading(true);
        setError('');
        try {
            const res = await translateText(sourceText, targetLang, undefined);
            setTranslated(res.data?.translated_text || '');
        } catch (e: any) {
            setError(e?.response?.data?.detail || t('翻訳に失敗しました。'));
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                <div className="bg-[#800020] px-6 py-4 flex justify-between items-center">
                    <h3 className="text-xl font-bold text-white tracking-widest">{t('翻訳')}</h3>
                    <button onClick={onClose} className="text-white/80 hover:text-white text-2xl">&times;</button>
                </div>
                <div className="p-6 space-y-4">
                    <div className="flex items-center gap-3">
                        <label className="text-sm font-semibold text-gray-700">{t('翻訳先')}</label>
                        <select
                            className="border rounded px-3 py-2 text-sm"
                            value={targetLang}
                            onChange={(e) => setTargetLang(e.target.value)}
                        >
                            {LANGUAGE_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                        <Button onClick={handleTranslate} disabled={loading}>
                            {loading ? t('翻訳中...') : t('翻訳する')}
                        </Button>
                    </div>
                    {error && <div className="text-sm text-red-600">{error}</div>}
                    {translated ? (
                        <div className="bg-gray-50 border rounded p-4 text-sm whitespace-pre-wrap">{translated}</div>
                    ) : (
                        <div className="text-xs text-gray-400">{t('翻訳結果がここに表示されます')}</div>
                    )}
                </div>
            </div>
        </div>
    );
}

